import React, { useContext, useState, useMemo, useCallback } from 'react';
import moment from 'moment-timezone';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
  Loader,
  VisitContents,
  AttributesIcon,
  usePrintDispatch,
} from '@mummssoftware/common-ui';
import { DeleteVisitConfirmation } from '@mummssoftware/common-ui/DeleteVisitConfirmation';
import { hhmmToMin } from '@mummssoftware/utils/timeUtils';
import ManifestWidget from '@mummssoftware/manifest';
import { IconButton } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  error,
  Edit,
  Delete,
  Print,
  Completed,
  InProgress,
  ReopenVisit,
  DocumentVisit,
} from '@mummssoftware/utils/constants';
import { isUserImpersonating, isMummsAdmin } from '@mummssoftware/utils/auth';
import { getVisitByVisitId } from '@mummssoftware/utils/pimReader';
import {
  VisitsGlobalContext,
  GlobalContext,
  PNGlobalContext,
  SET_PATIENT_VISITS,
  SET_REFRESH_VISIT,
  SET_EDITING_PAST_VISIT,
  REFRESH_PN_DATA,
} from '@mummssoftware/global-context';
import {
  SET_GLOBAL_MEASURES,
  RELOAD_MANIFEST,
  RELOAD_MEASURES,
} from '@mummssoftware/global-context/GlobalContext';
import {
  canAccptHopeVisitBeReopened,
  canDeclinedHopeVisitBeReopened,
  getHopeAttributes,
  getHopeMilestoneKeyStringFromReopenVisitAttributes,
} from '@mummssoftware/utils/hope';
import { Printing } from '@mummssoftware/utils';
import { createPortal } from 'react-dom';
import { asyncFallback, isActiveVisitStatus } from '@mummssoftware/utils/utils';
import { EditHCPCSDialog } from '../EditVisit/HCPCS/EditHCPCSDialog';
import { DeleteVisitCheckConfirmation } from '../DeleteVisit/DeleteVisitCheckConfirmation';
import { EditVisitConfirmation } from '../EditVisit/EditVisitConfirmation';
import { AppContext } from '../../../context';
import {
  ActionsContainer,
  VisitDetailRowWrapper,
  VisitDetailTableWrapper,
} from '../../../components/layout/WrapperDiv';
import { VisitLogContext } from '../../Context/VisitLogContext';
import { EditFTF } from './EditFTF';
import { pimFailed, deleteError } from '../../../translations/en.json';
import {
  SET_EDITING_ROW,
  SET_API_ERROR,
  ADD_DETAILS,
  REMOVE_DETAILS,
  SET_ATTRIBUTES,
  SET_SHOW_LOADER,
  SET_DATE_ERROR,
} from '../../Context/constants';
import {
  formatAttributesLowercase,
  updateVisitAttribute,
  updatePastPatientVisit,
  getEditableAttributes,
} from '../../Context/visitLogUtils';

export interface VisitLogDetailRowProps {
  data: mumms.Visit;
  node: any;
}
const ICON_STYLE = {
  display: 'flex',
  fontSize: 18,
  paddingTop: '0px',
} as const;

const VisitLogDetailRow = ({ data: visit, node }: VisitLogDetailRowProps) => {
  const { id: visitId, isdetailincluded, patientnumber } = visit;

  const {
    editingRow,
    editingAttributes,
    detailsCache,
    dispatch,
    toggleF2F,
    resetUI,
    resetVisitAttributes,
    hopePatientData,
  } = useContext(VisitLogContext);
  const globalContext = useContext(GlobalContext);
  const { appVisit } = globalContext;
  const {
    keycloak,
    agency,
    patient,
    pim,
    observation,
    visitAttributes,
    currentUser,
    readOnly,
    hydrateInProgressVisitFromLog,
    hydrateDocumentVisitFromLog,
    setReopenVisitDashboardLoader,
  } = useContext(AppContext);

  const {
    dispatchVG,
    transitFees,
    travelMileage,
    travelDuration,
    tollsFee,
    ccTime,
    patientVisitHistory,
    timerDuration,
    startTime,
    visitTypeOption,
    visitTypeMapping,
  } = useContext(VisitsGlobalContext);
  const pnGlobalContext = useContext(PNGlobalContext);

  // State
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [finalDeleteOpen, setFinalDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingPrint, setLoadingPrint] = useState(false);
  // check first if details exist in our cache (edits stored there)
  // if not fetched in VisitLogTable and cached, they must be included in the dto
  // Memoized values
  const details = useMemo(
    () => detailsCache[visitId] || (isdetailincluded ? visit : null),
    [detailsCache, visitId, isdetailincluded, visit],
  );

  const editing = editingRow === (visitId as any);

  const printingService = useMemo(
    () =>
      keycloak
        ? new Printing({
            url: window.env.hosts.printingService,
            keycloak,
            agency,
            currentUser,
          })
        : null,
    [keycloak, agency, currentUser],
  );

  const { isVisitOwner, userHasPerms } = useMemo(() => {
    const { personNumber, isHospiceAdmin } = currentUser;
    const { personnumber } = visit;
    const owner = personNumber === personnumber;
    const isAdmin = isMummsAdmin(keycloak?.tokenParsed as any);
    return {
      isVisitOwner: owner,
      userHasPerms: owner || isAdmin || isHospiceAdmin,
    };
  }, [currentUser, visit, keycloak]);

  const completedVisit = visit.status === Completed;
  const inProgVisit = isActiveVisitStatus(visit.status);

  const [acceptedAtts, refusedHopeAtts] = useMemo(
    () => getHopeAttributes(visit.visitattrsumdto),
    [visit.visitattrsumdto],
  );
  const isHopeVisit = Boolean(acceptedAtts?.length);
  // take the first one of acceptedAtts => [HUV1,SFVRQ]
  const acceptedAttsObj = isHopeVisit
    ? acceptedAtts[acceptedAtts?.length - 1]
    : undefined;
  // console.log('acceptedAtts', acceptedAtts);
  // console.log('refusedHopeAtts', refusedHopeAtts);

  const canAccptHopeVisitBeReopen = useMemo(() => {
    if (!acceptedAttsObj || !hopePatientData) return undefined;
    return canAccptHopeVisitBeReopened(
      acceptedAttsObj.shortname,
      hopePatientData,
    );
  }, [acceptedAttsObj, hopePatientData]);

  const canReopenDeclinedVisit = useMemo(() => {
    const refusedShortname = refusedHopeAtts[0]?.shortname;
    if (!refusedShortname || !hopePatientData) return undefined;
    return canDeclinedHopeVisitBeReopened(refusedShortname, hopePatientData);
  }, [refusedHopeAtts, hopePatientData]);

  /**
   * HOPE gating for "Reopen visit":
   * - SFV milestone (e.g. `HUV1-SFV`, including SFVRQ-paired visits) → always locked.
   * - Accepted HOPE attr → locked once the milestone is approved.
   * - Declined HOPE attr → locked once the milestone moved past pre-visit
   *   waiting ('Waiting Admit Visit' / 'Waiting HUV1 Visit' / 'Waiting HUV2 Visit').
   * - Otherwise unlocked (incl. no HOPE data, or unknown milestone).
   */

  const isHopeStatusLocked = useMemo(() => {
    if (canAccptHopeVisitBeReopen === false) return true;
    if (canAccptHopeVisitBeReopen === true) return false;
    if (canReopenDeclinedVisit === false) return true;
    if (canReopenDeclinedVisit === true) return false;
    return false;
  }, [canAccptHopeVisitBeReopen, canReopenDeclinedVisit]);
  const canUserEdit = userHasPerms && completedVisit && visit.roleshortname;

  /** Visit widget already has a visit open (`GlobalContext.appVisit`). */
  const userHasOtherInProgVisit = Boolean(appVisit);

  /**
   * Document capability — visit owner only (admins may Edit but not Document):
   * - owner + completed + role
   * - this session has no open visit widget
   */
  const canDocumentVisit =
    isVisitOwner &&
    completedVisit &&
    visit.roleshortname &&
    !userHasOtherInProgVisit;
  // if not a HOPE visit, then show reopen visit, only if HOPE VISIT accepted approved attribute can't reopen
  const canShowReopenVisit = useMemo(() => {
    if (readOnly || !completedVisit) return false;
    if (!userHasPerms) return false;
    if (userHasOtherInProgVisit) return false;
    if (isHopeStatusLocked) return false;
    return true;
  }, [
    readOnly,
    completedVisit,
    userHasPerms,
    userHasOtherInProgVisit,
    isHopeStatusLocked,
  ]);

  // Callbacks
  const fetchPatient = useCallback(
    () => pim.getPatientHeader(patientnumber, agency),
    [pim, patientnumber, agency],
  );

  const getPrintPromise = useCallback(
    (faxInstructions: any, emailInstructions: any) =>
      asyncFallback<mumms.patient>(patient, fetchPatient).then((jPatient) =>
        printingService!.printVisits(
          [details!],
          jPatient,
          faxInstructions,
          emailInstructions,
        ),
      ),
    [patient, fetchPatient, printingService, details],
  );

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    () => setLoadingPrint(true),
    () => setLoadingPrint(false),
  );

  const onPrint = useCallback(
    (option: string) => {
      if (!printingService || !details) return;
      if (option === 'Fax') fax();
      else if (option === 'Email') email();
      else print();
    },
    [printingService, details, print, fax, email],
  );

  const MENU_OPTIONS = useMemo(() => {
    if (readOnly) return [Print, 'Fax', 'Email'];
    const afterPrint = [Edit, DocumentVisit];
    if (canShowReopenVisit) afterPrint.push(ReopenVisit);
    afterPrint.push(Delete);
    return [Print, 'Fax', 'Email', ...afterPrint];
  }, [readOnly, canShowReopenVisit]);

  const refreshVisitWidgets = useCallback(() => {
    pnGlobalContext.dispatchPN({
      type: REFRESH_PN_DATA,
    });
    globalContext.dispatch({
      type: SET_GLOBAL_MEASURES,
      payload: null,
    });
    globalContext.dispatch({
      type: RELOAD_MANIFEST,
    });
    globalContext.dispatch({
      type: RELOAD_MEASURES,
    });
  }, [pnGlobalContext, globalContext]);

  const postEditedVisit = useCallback(async () => {
    // todo all the mapping
    const continuouscarehours = ccTime ? hhmmToMin(ccTime[0], ccTime[1]) : 0;
    const duration =
      Number.parseInt(timerDuration[0]) * 60 +
      Number.parseInt(timerDuration[1]);
    const visittype =
      visitTypeOption.value || visit.visitdisplaytype || visit.visittype;
    const visitdisplay =
      visittype && visitTypeMapping ? visitTypeMapping[visittype] : visittype;

    const updatedPriorVisit: any = {
      startDate: startTime,
      tolls: tollsFee || (visit as any).tolls,
      transitFees: transitFees || (visit as any).publictransit,
      travelMileage: travelMileage || (visit as any).mileage,
      travelDuration: travelDuration || (visit as any).traveltime,
      duration: duration || (visit as any).durationmins,
      continuouscarehours:
        continuouscarehours || (visit as any).continuouscarehours,
      displayvisittype: visitdisplay,
      visitType: visitdisplay,
    };

    const updatedVisitRes = await updatePastPatientVisit(
      pim,
      updatedPriorVisit,
      visit,
      agency,
      keycloak,
      toggleF2F,
    );
    if (updatedVisitRes && updatedVisitRes[0]) {
      const visitAns = updatedVisitRes[0];
      dispatch({
        type: ADD_DETAILS,
        payload: visitAns,
      });
      dispatchVG({
        type: SET_REFRESH_VISIT,
      });
      await observation
        .syncPatientVisits(visitAns.id, visit.effectivedatetime, agency)
        .then(async (resp: any) => {
          if (!(resp instanceof Response)) {
            // refreshVisitWidgets();
          }
        });
      resetUI();
    } else {
      dispatch({
        type: SET_API_ERROR,
        payload: {
          text: updatedVisitRes.message || pimFailed,
          severity: error,
        },
      });
    }
    dispatch({
      type: SET_SHOW_LOADER,
      payload: false,
    });
  }, [
    pim,
    editingAttributes,
    visit,
    transitFees,
    travelMileage,
    travelDuration,
    agency,
    dispatch,
    details,
    resetUI,
    refreshVisitWidgets,
  ]);

  const postVisitAttributeEdits = useCallback(
    async (event: React.MouseEvent) => {
      // event.preventDefault();
      const updatedVisit = await updateVisitAttribute(
        pim,
        editingAttributes,
        visit,
        agency,
      );

      if (!updatedVisit) {
        dispatch({
          type: SET_API_ERROR,
          payload: {
            text: pimFailed,
            severity: error,
          },
        });
      } else {
        dispatch({
          type: ADD_DETAILS,
          payload: {
            ...details,
            visitattrsumdto: JSON.stringify(
              formatAttributesLowercase(updatedVisit.visitAttributes),
            ),
            facetofacevisit: toggleF2F,
          },
        });
        resetVisitAttributes();
      }
    },
    [pim, editingAttributes, visit, agency, dispatch, details, resetUI],
  );

  const handleSettings = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const validEditVisit = () => {
    dispatch({
      type: SET_SHOW_LOADER,
      payload: true,
    });
    postEditedVisit();
  };

  const onEditVisit = () => {
    if (startTime) {
      const originalDate = visit.effectivedatetime;
      if (
        moment(startTime).diff(moment(originalDate), 'days') > 10 ||
        moment(originalDate).diff(moment(startTime), 'days') > 10
      ) {
        dispatch({
          type: SET_DATE_ERROR,
          payload: true,
        });
      } else {
        validEditVisit();
      }
    } else {
      validEditVisit();
    }
  };
  const onCheckDelete = () => {
    setFinalDeleteOpen(true);
  };
  // todo hook CASCADE DELETE v2
  const onDelete = useCallback(async () => {
    if (!patient || !visit) return;

    try {
      const deleteRes = await pim.deletePatientVisit(
        patient.patientNumber,
        visit.id,
        agency,
      );

      const deleteFailed =
        !deleteRes || (deleteRes instanceof Response && !deleteRes?.ok);

      if (!deleteFailed) {
        setDeleteOpen(false);
        dispatchVG({
          type: SET_PATIENT_VISITS,
          payload: (patientVisitHistory ?? []).filter(
            (historyVisit: mumms.pimreaderVisit) =>
              historyVisit.id !== visit.id,
          ),
        });
      } else {
        throw new Error('Delete failed');
      }
    } catch {
      dispatch({
        type: SET_API_ERROR,
        payload: {
          text: deleteError,
          severity: error,
        },
      });
    }
    setFinalDeleteOpen(false);
  }, [patient, visit, pim, agency, dispatch, dispatchVG, patientVisitHistory]);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    dispatch({
      type: SET_EDITING_ROW,
      payload: null,
    });
  }, []);

  const handleEditOption = useCallback(async () => {
    if (!details) return;
    setAnchorEl(null);
    // const pimVisit = await pim.getPatientVisit(visit.patient_id, visit, agency);
    globalContext.dispatch({
      type: SET_EDITING_PAST_VISIT,
      payload: details,
    });
    setEditOpen(true);

    dispatch({
      type: SET_ATTRIBUTES,
      payload: getEditableAttributes(
        {
          visitDetails: details,
        },
        visitAttributes,
      ),
    });
    dispatch({
      type: SET_EDITING_ROW,
      payload: visitId,
    });
  }, [details, dispatch, visitAttributes, visitId]);

  const handleDeleteOption = useCallback(() => {
    setAnchorEl(null);
    setDeleteOpen(true);
  }, []);

  /**
   * Shared PIM sequence for reopen and document-from-log: load full DTO, PUT In Progress,
   * refresh pimreader row, update visit-log grid, optionally hydrate embedded dashboard.
   */
  const setCompletedVisitInProgressFromLog = useCallback(
    async (
      buildUpdatedVisit: (pimVisit: any) => mumms.PatientVisitDTO,
      hydrateDashboard?: (
        visitAns: any,
        updatedVisit: mumms.PatientVisitDTO,
      ) => void,
    ): Promise<boolean> => {
      if (!patient || !visit || !keycloak) return false;

      let hydratedDashboard = false;
      dispatch({
        type: SET_SHOW_LOADER,
        payload: true,
      });
      if (setReopenVisitDashboardLoader) {
        setReopenVisitDashboardLoader(true);
      }
      try {
        const pimVisit = await pim.getPatientVisit(
          visit.patient_id,
          visit,
          agency,
        );
        if (pimVisit instanceof Response) {
          throw new Error();
        }
        const updatedVisit = buildUpdatedVisit(pimVisit);
        const updatedVisitRes = await pim.updatePatientVisit(
          visit.patientnumber,
          updatedVisit,
          agency,
        );
        if (updatedVisitRes instanceof Response) {
          const errBody = await updatedVisitRes.json().catch(() => ({}));
          dispatch({
            type: SET_API_ERROR,
            payload: {
              text:
                (errBody as {
                  message?: string;
                })?.message || pimFailed,
              severity: error,
            },
          });
          return false;
        }
        const newVisit = await getVisitByVisitId(
          String(visit.id),
          agency,
          keycloak,
        );
        if (!newVisit || newVisit instanceof Response) {
          dispatch({
            type: SET_API_ERROR,
            payload: {
              text: pimFailed,
              severity: error,
            },
          });
          return false;
        }
        const visitAns = Array.isArray(newVisit) ? newVisit[0] : newVisit;
        if (visitAns) {
          dispatch({
            type: REMOVE_DETAILS,
            payload: visit.id,
          });
          dispatchVG({
            type: SET_PATIENT_VISITS,
            payload: (patientVisitHistory ?? []).map(
              (historyVisit: mumms.pimreaderVisit) =>
                historyVisit.id === visit.id
                  ? {
                      ...historyVisit,
                      ...visitAns,
                    }
                  : historyVisit,
            ),
          });
          if (hydrateDashboard) {
            hydrateDashboard(visitAns, updatedVisit);
            hydratedDashboard = true;
          }
        }
        return hydratedDashboard;
      } catch {
        dispatch({
          type: SET_API_ERROR,
          payload: {
            text: pimFailed,
            severity: error,
          },
        });
        return false;
      } finally {
        dispatch({
          type: SET_SHOW_LOADER,
          payload: false,
        });
        if (!hydratedDashboard && setReopenVisitDashboardLoader) {
          setReopenVisitDashboardLoader(false);
        }
      }
    },
    [
      patient,
      visit,
      pim,
      agency,
      keycloak,
      dispatch,
      dispatchVG,
      patientVisitHistory,
      setReopenVisitDashboardLoader,
    ],
  );

  /**
   * Reopen a completed visit by setting its PIM status back to In Progress.
   *
   * Step-by-step (each is needed for the overall flow to be correct and consistent):
   *
   * 1. **Close the action menu (`setAnchorEl(null)`)** — The user chose an item from
   *    the Material-UI `Menu`; clearing the anchor dismisses it so the UI does not stay
   *    open or trap focus after the async work starts.
   *
   * 2. **Guard `patient`, `visit`, `keycloak`** — Without patient/visit we cannot call
   *    PIM or know which row to update. Without keycloak we cannot call
   *    `getVisitByVisitId` after the save; bail early to avoid partial errors.
   *
   * 3. **`SET_SHOW_LOADER` true** — Gives feedback for the multi-request sequence
   *    (GET visit, PUT visit, GET pimreader row, observation sync). `finally` clears it
   *    so the loader is never stuck on failure or success.
   *
   * 4. **`pim.getPatientVisit`** — PIM `PUT visits_v2` expects a full visit DTO. The grid
   *    row is a summary; we must load the canonical payload from PIM before patching
   *    `status`, same pattern as `updatePastPatientVisit` elsewhere in visit-log.
   *
   * 5. **Reject `instanceof Response`** — `getPatientVisit` signals HTTP/API failure with
   *    a `Response`; treat as error instead of spreading into `reopened`.
   *
   * 6. **`{ ...pimVisit, status: InProgress }`** — Only the status changes; other fields
   *    must stay as PIM has them so we do not drop attributes, HCPCS, dates, etc.
   *
   * 7. **`pim.updatePatientVisit`** — Persists the reopened visit; this is the actual
   *    backend state change.
   *
   * 8. **Handle failed PUT** — On error response, surface `message` if present (matches
   *    `updatePastPatientVisit`) and return without treating the visit as reopened.
   *
   * 9. **`getVisitByVisitId`** — PIM returns a patient-visit shape; visit-log and
   *    pimreader views use a different visit-row shape for the grid. Refreshing via
   *    pimreader aligns the cache and master row with what the rest of the app expects
   *    (`postEditedVisit` uses the same pattern).
   *
   * 10. **`REMOVE_DETAILS`** — Clears `detailsCache` for this visit so the next row
   *     expand runs `getvisits` instead of showing a stale pre-reopen snapshot.
   *
   * 11. **`SET_PATIENT_VISITS` (map)** — Updates the ag-grid row data for this visit id
   *     so the list shows “In Progress” without a full refetch.
   *
   * 12. **Dashboard: `hydrateInProgressVisitFromLog(visitAns, reopened, hopeMilestone?)`** —
   *     Opens the visit widget and sets `SET_SAVED_VISIT` / `SET_INITIAL_SAVED_VISIT` to the
   *     PIM DTO (`reopened`) and `SET_APP_VISIT` to the pimreader row (via `loadInProgress`),
   *     matching `reopenVisit` / start-of-visit flows. When HOPE reopen rules apply, the
   *     optional third argument is the milestone key (`ADM`, `HUV1-SFV`, …) for Hope
   *     context so milestones are not derived only from `getAvailableMilestones`. Embedded
   *     dashboard only; standalone visit-log uses `SET_APP_VISIT` with `visitAns` only.
   *
   *     Reopen does **not** dispatch `SET_REFRESH_VISIT`: the grid is updated via
   *     `SET_PATIENT_VISITS`, and `VisitContext`’s `refreshVisits` effect is left unchanged.
   *
   */
  const handleReopenVisit = useCallback(async (): Promise<void> => {
    setAnchorEl(null);
    await setCompletedVisitInProgressFromLog(
      (pimVisit) => ({
        // todo calculate doc time and add to the payload
        ...pimVisit,
        status: InProgress,
      }),
      (visitAns, reopened) => {
        if (!hydrateInProgressVisitFromLog) return;
        let hopeReopenWorkflowMilestone: string | undefined;
        if (
          canAccptHopeVisitBeReopen === true ||
          canReopenDeclinedVisit === true
        ) {
          const m = getHopeMilestoneKeyStringFromReopenVisitAttributes(
            acceptedAtts,
            refusedHopeAtts,
          );
          if (m) hopeReopenWorkflowMilestone = m;
        }
        hydrateInProgressVisitFromLog(
          visitAns,
          reopened,
          hopeReopenWorkflowMilestone,
        );
      },
    );
  }, [
    setCompletedVisitInProgressFromLog,
    hydrateInProgressVisitFromLog,
    acceptedAtts,
    refusedHopeAtts,
    canAccptHopeVisitBeReopen,
    canReopenDeclinedVisit,
  ]);

  const handleDocumentVisit = useCallback(async (): Promise<void> => {
    setAnchorEl(null);
    if (!canDocumentVisit) return;
    await setCompletedVisitInProgressFromLog(
      (pimVisit) => ({
        // todo calculate doc time and add to the payload
        ...pimVisit,
        status: InProgress,
        reOpened: true,
      }),
      (visitAns, updatedVisit) => {
        if (hydrateDocumentVisitFromLog) {
          hydrateDocumentVisitFromLog(visitAns, updatedVisit);
        }
      },
    );
  }, [
    setCompletedVisitInProgressFromLog,
    hydrateDocumentVisitFromLog,
    canDocumentVisit,
  ]);
  const onCancelEdit = () => {
    setEditOpen(false);
    resetUI();
  };

  const selectOption = useCallback(
    (option: string) => {
      switch (option) {
        case Print:
          return onPrint(Print);
        case 'Fax':
          return onPrint('Fax');
        case 'Email':
          return onPrint('Email');
        case Edit:
          return handleEditOption();
        case Delete:
          return handleDeleteOption();
        case ReopenVisit:
          return handleReopenVisit();
        case DocumentVisit:
          return handleDocumentVisit();
        default:
          return null;
      }
    },
    [
      onPrint,
      handleEditOption,
      handleDeleteOption,
      handleReopenVisit,
      handleDocumentVisit,
    ],
  );

  const isDisabledOption = useCallback(
    (option: string) =>
      (inProgVisit && (option === Print || option === 'Fax')) ||
      (option === Edit && !canUserEdit) ||
      (option === DocumentVisit && !canDocumentVisit) ||
      (option === Delete && (inProgVisit || !currentUser.isHospiceAdmin)),
    [canUserEdit, canDocumentVisit, inProgVisit, currentUser.isHospiceAdmin],
  );

  const getAttributes = () => (
    <AttributesIcon
      visitAttributes={editingAttributes}
      appVisit={details || visit}
      onSubmit={postVisitAttributeEdits}
    />
  );
  // todo how to see
  const renderEditF2F = useCallback(
    () => (details ? <EditFTF data={details} /> : null),
    [details],
  );
  // @note we should refactor this component and consolidate the greatly expanded logic
  // @note contd. now includes the `canDelete` logic, print loading, editing, and whether to fetch details
  // todo LOADER
  // Early return for loading state
  if (!details) {
    return (
      <VisitDetailRowWrapper>
        <VisitDetailTableWrapper>
          <Loader />
        </VisitDetailTableWrapper>
      </VisitDetailRowWrapper>
    );
  }

  return (
    <VisitDetailRowWrapper>
      {loadingPrint && <Loader />}

      <ActionsContainer>
        <IconButton
          onClick={handleSettings}
          size="small"
          disabled={editing}
          style={ICON_STYLE}
        >
          <SettingsIcon style={ICON_STYLE} />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option}
              onClick={() => selectOption(option)}
              disabled={isDisabledOption(option)}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      </ActionsContainer>

      <DeleteVisitCheckConfirmation
        open={deleteOpen}
        setClosed={() => setDeleteOpen(false)}
        onDelete={onCheckDelete}
        data={visit}
        isHopeVisit={isHopeVisit}
      />
      <EditVisitConfirmation
        open={editOpen}
        setClosed={onCancelEdit}
        onEdit={onEditVisit}
        onRecord={validEditVisit}
        data={details}
        isHopeVisit={isHopeVisit}
        getAttributes={getAttributes}
      />
      <DeleteVisitConfirmation
        open={finalDeleteOpen}
        setClosed={() => {
          setFinalDeleteOpen(false);
        }}
        onDelete={onDelete}
        okToDelete
      />
      <VisitContents
        dto={details}
        editing={false}
        editF2F={renderEditF2F}
        agency={agency}
        keycloak={keycloak}
        patientNumber={patient?.patientNumber}
        ManifestWidget={ManifestWidget}
      />
      <EditHCPCSDialog />
    </VisitDetailRowWrapper>
  );
};
export default React.memo(
  VisitLogDetailRow,
  (prevProps, nextProps) =>
    // Only re-render if visit id or node changes
    prevProps.data.id === nextProps.data.id &&
    prevProps.node === nextProps.node,
);
